import React, { useEffect, useState } from 'react';
import { ProgressBar, Pagination } from 'react-bootstrap';
import './Holders.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import AddressDisplay from '../addressdisplay/AddressDisplay';
import { RiContractLine } from "react-icons/ri";

const Contract = RiContractLine as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface PriceBoxProps {
  ca: string;
  chain: string;
}

interface HolderDataResult {
  owner_address: string;
  owner_address_label: string;
  balance_formatted: string;
  usd_value: string;
  is_contract: boolean;
  percentage_relative_to_total_supply: string;
}

interface HolderData {
  result: HolderDataResult[];
}

const Holders: React.FC<PriceBoxProps> = ({ ca, chain }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [holderData, setHolderData] = useState<HolderData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        let response;
        if (chain === 'sol'){
          // If Solana is not supported here, simply return or handle accordingly.
          return;
        } else {
          // Call your Netlify Function instead of the external API directly:
          response = await fetch(
            `/.netlify/functions/fetch-holders?ca=${ca}&chain=${chain}`
          );
        }

        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const data: HolderData = await response.json();
        setHolderData(data);
      } catch (err: any) {
        setError(err.message || 'An error occurred.');
      } finally {
        setLoading(false);
      }
    };

    if (ca && chain) {
      fetchData();
    }
  }, [ca, chain]);

  const formatVolume = (volume: number | undefined): string => {
    if (volume == null) return '-';

    if (Math.abs(volume) >= 1_000_000_000) {
      return (
        (volume / 1_000_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'B'
      );
    } else if (Math.abs(volume) >= 1_000_000) {
      return (
        (volume / 1_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'M'
      );
    } else if (Math.abs(volume) >= 1_000) {
      return (
        (volume / 1_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'K'
      );
    }
    return volume.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className='not-found' style={{margin:'15px'}}>We ran into an issue, please try again later... 😥</div>;
  }

  if (!holderData || !holderData.result) {
    return (
      <div className="token-security" style={{ padding: "15px" }}>
        <div className="not-found">
          No holder data available yet... 😥
        </div>
      </div>
    );
  }

  if (holderData.result.length === 0) {
    return (
      <div className="token-security" style={{ padding: "15px" }}>
        <div className="not-found">
          No holder data available yet... 😥
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="token-security">
        {holderData && holderData.result.length > 0 ? (
          holderData.result.map((item, index) => (
            <div key={index} className="scanner-data">
              <div>
                {index + 1}. {item.is_contract && <Contract style={{ color: "rgb(203, 197, 14)" }} />} {item.owner_address_label || <AddressDisplay address={item.owner_address} />} 
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {formatVolume(Number(item.balance_formatted))} ({Number(item.percentage_relative_to_total_supply).toFixed(1)}%) <span className='usd-val'>${formatVolume(Number(item.usd_value))}</span>
              </div>
            </div>
          ))
        ) : (
          "Loading..."
        )}
      </div>
    </>
  );
};

export default Holders;
